"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FaArrowLeft, FaMapMarkerAlt } from "react-icons/fa";
import { Button } from "@/components/ui";
import AddressInput from "@/components/ui/address-input";
import { usePurchaseAuth } from "@/contexts/purchase-auth-provider";
import { parseError } from "@/lib/parse-error";
import type { PurchaseBuyer } from "@/types/shop";
import type { ShopShippingAddressUpdate } from "@/api/patient/portal";
import { updateShopShippingAddress } from "@/api/patient/portal";
import { patientPortalQueryKeys } from "@/api/patient/portal-query";
interface ShippingAddressStepProps {
  onBack: () => void;
  onContinue: (buyer: PurchaseBuyer) => void;
  savedBuyer?: PurchaseBuyer | null;
}
const hasCompleteAddress = (buyer: PurchaseBuyer): boolean => Boolean(buyer.address?.trim() && buyer.city?.trim() && buyer.state?.trim() && buyer.zipCode?.trim());
const addressLines = (buyer: PurchaseBuyer): string[] => [buyer.address ?? "", buyer.addressLineTwo ?? "", `${[buyer.city, buyer.state].filter(Boolean).join(", ")} ${buyer.zipCode ?? ""}`.trim()].filter(Boolean);
export default function ShippingAddressStep({
  onBack,
  onContinue,
  savedBuyer = null
}: ShippingAddressStepProps) {
  const {
    buyer,
    isAuthenticated,
    isBuyerLoading,
    ensureAuthenticated
  } = usePurchaseAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const form = useForm<ShopShippingAddressUpdate>({
    defaultValues: {
      address: "",
      addressLineTwo: "",
      city: "",
      state: "",
      zipCode: ""
    }
  });
  const saveAddress = useMutation({
    mutationFn: updateShopShippingAddress
  });
  const displayBuyer = savedBuyer ?? buyer;
  React.useEffect(() => {
    if (!displayBuyer) return;
    form.reset({
      address: displayBuyer.address ?? "",
      addressLineTwo: displayBuyer.addressLineTwo ?? "",
      city: displayBuyer.city ?? "",
      state: displayBuyer.state ?? "",
      zipCode: displayBuyer.zipCode ?? ""
    });
  }, [displayBuyer, form]);
  const handleSubmit = form.handleSubmit(async values => {
    setSubmitError(null);
    try {
      const patient = await saveAddress.mutateAsync(values);
      if (!patient) throw new Error("We couldn't save your shipping address.");
      const updatedBuyer: PurchaseBuyer = {
        ...buyer!,
        address: patient.address ?? null,
        addressLineTwo: patient.addressLineTwo ?? null,
        city: patient.city ?? null,
        state: patient.state ?? null,
        zipCode: patient.zipCode ?? null
      };
      if (!hasCompleteAddress(updatedBuyer)) {
        throw new Error("Enter a complete shipping address.");
      }
      queryClient.setQueryData(patientPortalQueryKeys.me(updatedBuyer.id), patient);
      onContinue(updatedBuyer);
    } catch (error) {
      setSubmitError(await parseError(error, "We couldn't save your shipping address. Please try again."));
    }
  });
  const showForm = Boolean(displayBuyer && (isEditing || !hasCompleteAddress(displayBuyer)));
  return <div className="motion-preset-slide-right-sm mx-auto max-w-xl">
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 cursor-pointer px-0 text-foreground-secondary">
        <FaArrowLeft className="mr-2 text-xxs" />
        <span>Back</span>
      </Button>

      <section className="py-2">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-background-secondary text-primary">
            <FaMapMarkerAlt className="h-4 w-4" />
          </div>
          <div>
            <p className="text-overline text-primary">Shipping address</p>
            <h2 className="mt-1 text-h3 text-foreground">
              Where should we send your order?
            </h2>
            <p className="mt-2 text-body text-foreground-secondary">
              Confirm the address on file or enter a different one.
            </p>
          </div>
        </div>

        {!isAuthenticated || !buyer ? <div className="mt-6">
            <p className="text-body-strong text-foreground">
              Sign in to continue
            </p>
            <p className="mt-1 text-caption text-foreground-secondary">
              We need to verify it&apos;s you before loading your address.
            </p>
            <Button variant="primary" onClick={() => ensureAuthenticated()} className="mt-4 cursor-pointer">
              Sign in
            </Button>
          </div> : isBuyerLoading && !savedBuyer ? <p className="mt-6 text-body text-foreground-secondary">
            Loading your shipping address…
          </p> : showForm ? <FormProvider {...form}>
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <AddressInput required defaultExpanded label="Shipping address" />
              {submitError && <p role="alert" className="text-caption text-status-error-text">
                  {submitError}
                </p>}
              <div className="flex flex-wrap gap-3">
                <Button type="submit" variant="primary" disabled={saveAddress.isPending} className="cursor-pointer">
                  {saveAddress.isPending ? "Saving address…" : "Save and continue"}
                </Button>
                {displayBuyer && hasCompleteAddress(displayBuyer) && <Button type="button" variant="secondary" onClick={() => setIsEditing(false)} disabled={saveAddress.isPending} className="cursor-pointer text-primary">
                    Cancel
                  </Button>}
              </div>
            </form>
          </FormProvider> : <div className="mt-6">
            <div>
              <p className="text-body-strong text-foreground">
                {[displayBuyer!.firstName, displayBuyer!.lastName].filter(Boolean).join(" ")}
              </p>
              {addressLines(displayBuyer!).map(line => <p key={line} className="mt-1 text-body text-foreground-secondary">
                  {line}
                </p>)}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button variant="primary" onClick={() => onContinue(displayBuyer!)} className="cursor-pointer">
                Use this address
              </Button>
              <Button variant="ghost" onClick={() => setIsEditing(true)} className="cursor-pointer text-primary">
                Change address
              </Button>
            </div>
          </div>}
      </section>
    </div>;
}